import React, { useEffect, useState, useMemo } from 'react';
import { message } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../redux/store';
import { useTheme } from '../../context/ThemeContext';
import { fetchDevices, fetchDeviceTypes, deleteDevice, setFilteredDevices, DevicesState } from '../../redux/Devices';
import { Device, StoreGroup, DevicePair } from './types';
import { DeviceForm } from './DeviceForm';
import { SearchFilters } from './SearchFilters';
import { StoreGroup as StoreGroupComponent } from './StoreGroup';
import { EmptyState } from './EmptyState';
import { 
  Container, 
  HeaderContainer, 
  TitleContainer, 
  Title, 
  Subtitle,
  AddButton,
  StoreGroupsContainer,
} from './styles';
import { LABELS, MESSAGES, DEVICE_TYPE_NAMES } from './constants';

const Devices: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isDarkMode } = useTheme();
  const { devices, deviceTypes, filteredDevices } = useSelector(
    (state: RootState): DevicesState => state.devices
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDevice, setCurrentDevice] = useState<Device | null>(null);
  const [selectedType, setSelectedType] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState<string>('');

  useEffect(() => {
    dispatch(fetchDevices());
    dispatch(fetchDeviceTypes());
  }, [dispatch]);

  const getDevicePairs = (devicesList: Device[]): Record<string, StoreGroup> => {
    if (!devicesList || !Array.isArray(devicesList) || devicesList.length === 0) {
      return {};
    }

    const devicesByStore = devicesList.reduce((acc, device) => {
      const storeId = typeof device.store === 'object' ? device.store?.id || 'unassigned' : device.store || 'unassigned';
      if (!acc[storeId]) {
        const storeName = typeof device.store === 'object' ? device.store?.name || 'Unassigned Devices' : 'Unassigned Devices';
        const merchantName = typeof device.merchant === 'object' ? device.merchant?.name || 'Unknown Merchant' : 'Unknown Merchant';
        acc[storeId] = {
          storeName,
          storeId,
          merchantName,
          devicePairs: [],
          otherDevices: [],
          _kiosks: [],
          _readers: [],
        } as StoreGroup & { _kiosks: Device[]; _readers: Device[] };
      }

      const deviceTypeName = typeof device.type === 'object' ? device.type?.name?.toLowerCase() : '';
      if (deviceTypeName === DEVICE_TYPE_NAMES.KIOSK) {
        (acc[storeId] as StoreGroup & { _kiosks: Device[] })._kiosks.push(device);
      } else if (deviceTypeName === DEVICE_TYPE_NAMES.READER) {
        (acc[storeId] as StoreGroup & { _readers: Device[] })._readers.push(device);
      } else {
        acc[storeId].otherDevices.push(device);
      }

      return acc;
    }, {} as Record<string, StoreGroup & { _kiosks: Device[]; _readers: Device[] }>);

    Object.keys(devicesByStore).forEach((storeId) => {
      const store = devicesByStore[storeId];
      const kiosks = store._kiosks || [];
      const readers = store._readers || [];

      const pairs: DevicePair[] = [];
      const maxPairs = Math.max(kiosks.length, readers.length);

      for (let i = 0; i < maxPairs; i++) {
        const kiosk = kiosks[i] || null;
        const reader = readers[i] || null;
        pairs.push({
          id: kiosk?.id || reader?.id || `pair-${i}`,
          kiosk,
          reader,
        });
      }

      store.devicePairs = pairs;
      delete (store as any)._kiosks;
      delete (store as any)._readers;
    });

    return devicesByStore as Record<string, StoreGroup>;
  };

  useEffect(() => {
    let filtered = [...devices];

    if (selectedType) {
      filtered = filtered.filter((device) => {
        const deviceTypeId = typeof device.type === 'object' ? device.type?.id : device.type;
        return deviceTypeId === selectedType;
      });
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter((device) => {
        const deviceName = device.name?.toLowerCase() || '';
        const deviceIdentifier = device.identifier?.toLowerCase() || '';
        const typeName = typeof device.type === 'object' ? device.type?.name?.toLowerCase() : '';
        const storeName = typeof device.store === 'object' ? device.store?.name?.toLowerCase() : '';
        const merchantName = typeof device.merchant === 'object' ? device.merchant?.name?.toLowerCase() : '';

        return (
          deviceName.includes(query) ||
          deviceIdentifier.includes(query) ||
          typeName.includes(query) ||
          storeName.includes(query) ||
          merchantName.includes(query)
        );
      });
    }

    dispatch(setFilteredDevices(filtered));
  }, [devices, selectedType, searchQuery, dispatch]);

  const devicePairs = useMemo(() => getDevicePairs(filteredDevices), [filteredDevices]);

  const handleTypeFilter = (value: string) => {
    setSelectedType(value);
  };

  const handleSearch = (value: string) => {
    setSearchQuery(value);
  };

  const handleDeleteDevice = async (deviceId: string) => {
    try {
      await dispatch(deleteDevice(deviceId)).unwrap();
      message.success(MESSAGES.DEVICE_DELETED);
    } catch (error: any) {
      message.error(error || MESSAGES.ERROR_DELETE);
    }
  };

  const handleEditDevice = (device: Device) => {
    setCurrentDevice(device);
    setIsModalOpen(true);
  };

  const handleAddDevice = () => {
    setCurrentDevice(null);
    setIsModalOpen(true);
  };

  const handleSave = () => {
    setIsModalOpen(false);
    setCurrentDevice(null);
    dispatch(fetchDevices());
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setCurrentDevice(null);
  };

  return (
    <Container>
      <HeaderContainer>
        <TitleContainer>
          <Title $isDark={isDarkMode}>{LABELS.DEVICE_MANAGER}</Title>
          <Subtitle $isDark={isDarkMode}>{LABELS.DEVICE_MANAGER_DESC}</Subtitle>
        </TitleContainer>
        <AddButton
          type="primary"
          icon={<FontAwesomeIcon icon={faPlus} />}
          onClick={handleAddDevice}
          size="large"
        >
          {LABELS.ADD_DEVICE}
        </AddButton>
      </HeaderContainer>

      <SearchFilters
        searchQuery={searchQuery}
        selectedType={selectedType}
        deviceTypes={deviceTypes}
        onSearchChange={handleSearch}
        onTypeFilterChange={handleTypeFilter}
      />

      {Object.keys(devicePairs).length === 0 ? (
        <EmptyState hasFilters={!!(searchQuery || selectedType)} isDark={isDarkMode} />
      ) : (
        <StoreGroupsContainer>
          {Object.values(devicePairs).map((storeGroup) => (
            <StoreGroupComponent
              key={storeGroup.storeId}
              storeGroup={storeGroup}
              isDark={isDarkMode}
              onEditDevice={handleEditDevice}
              onDeleteDevice={handleDeleteDevice}
            />
          ))}
        </StoreGroupsContainer>
      )}

      <DeviceForm
        open={isModalOpen}
        initialDevice={currentDevice}
        onSave={handleSave}
        onCancel={handleCancel}
      />
    </Container>
  );
};

export default Devices;
